import { Link } from 'react-router-dom';

interface QuickLink {
  title: string;
  description: string;
  subtitle: string;
  icon: string;
  path: string;
  label: string;
}

const QuickLinks = () => {
  const links: QuickLink[] = [
    {
      title: 'Clinical Services',
      description:
        'Regulated acupuncture services integrating structural assessment, nervous system modulation, and refined facial precision.',
      subtitle: 'Designed for sustained stability — not temporary correction.',
      icon: 'ri-heart-pulse-line',
      path: '/clinical-services',
      label: 'View Services',
    },
    {
      title: 'Botanical Collection',
      description:
        'A considered line of plant-based formulations designed to support nervous system balance and adaptive resilience.',
      subtitle: 'Rooted in clinical observation. Refined for everyday regulation.',
      icon: 'ri-leaf-line',
      path: '/product',
      label: 'Join Waitlist',
    },
    {
      title: 'Educational Programs',
      description:
        'For practitioners seeking a deeper understanding of facial structure, meridian intelligence, and botanical neuro-regulation.',
      subtitle: 'Structured education rooted in the Stability Method™ framework.',
      icon: 'ri-book-open-line',
      path: '/education',
      label: 'View Programs',
    },
  ];

  return (
    <section className="py-7 sm:py-16 px-4 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 sm:mb-12">
          <div className="w-8 h-[1px] bg-bronze"></div>
          <span className="text-bronze text-[9px] sm:text-[10px] font-sans font-semibold tracking-[0.3em] uppercase">
            Explore
          </span>
        </div>

        {/* List rows */}
        <div className="divide-y divide-warmGray/20 border-t border-warmGray/20">
          {links.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 py-5 sm:py-9 cursor-pointer hover:bg-stone-50/60 transition-colors duration-300 px-2 sm:px-4 -mx-2 sm:-mx-4 rounded-lg"
            >
              {/* Index number */}
              <span className="hidden sm:block text-[11px] font-sans text-bronze/50 tracking-widest w-12 flex-shrink-0">
                0{index + 1}
              </span>

              {/* Icon + Title */}
              <div className="flex items-center gap-3 sm:w-64 flex-shrink-0">
                <div className="w-9 h-9 flex items-center justify-center rounded-full border border-bronze/20 group-hover:border-bronze/50 group-hover:bg-bronze/5 transition-all duration-300">
                  <i className={`${link.icon} text-bronze text-sm`} aria-hidden="true"></i>
                </div>
                <h3 className="font-display text-base sm:text-lg font-light text-charcoal italic group-hover:text-bronze transition-colors duration-300">
                  {link.title}
                </h3>
              </div>

              {/* Description */}
              <div className="flex-1 sm:px-8">
                <p className="text-textGray text-[11px] sm:text-xs font-sans font-light leading-relaxed">
                  {link.description}
                </p>
                <p className="text-charcoal/50 text-[10px] sm:text-[11px] font-sans italic mt-1">
                  {link.subtitle}
                </p>
              </div>

              {/* CTA */}
              <div className="flex-shrink-0 sm:pl-6">
                <span className="inline-flex items-center gap-2 text-[9px] sm:text-[10px] font-sans font-semibold text-charcoal group-hover:text-bronze transition-colors duration-300 tracking-widest uppercase whitespace-nowrap border-b border-charcoal/20 group-hover:border-bronze/40 pb-0.5">
                  {link.label}
                  <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform duration-300" aria-hidden="true"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default QuickLinks;
